import {
  AssessmentColumnNameEnum,
  AssessmentResultStatusEnum,
} from "@/api/generated/types.gen";

export type AssessmentSection = {
  id: string;
  title: string;
  key?: AssessmentColumnNameEnum;
  templateIndex?: number[];
  /** Render this section last, after all resolved sections (MDRS-136). */
  pinLast?: boolean;
};

// Matches ServiceTypeSlug from @/constants/service-types (duplicated here to
// avoid a cross-constants import and to give SECTIONS_BY_SERVICE_TYPE compile-time
// exhaustiveness checking).
type ServiceTypeKey =
  | "capacity_assessment"
  | "comcare"
  | "dva"
  | "fitness_for_duty"
  | "institutional_abuse"
  | "ip_tpd"
  | "medical_negligence"
  | "motor_vehicle"
  | "pain_suffering_quantum"
  | "public_liability"
  | "workers_compensation";

// ─── Master section registry ─────────────────────────────────────────────────
// All sections across all service types. AudioTranscript and useAutoExport
// look up sections here by ID or key.

export const ASSESSMENT_SECTIONS: AssessmentSection[] = [
  // ── Shared / legacy sections (IDs 1-12) ──────────────────────────────────
  {
    id: "1",
    title: "Transcript",
    key: "full_transcript",
    templateIndex: [1, 2, 3],
  },
  {
    id: "2",
    title: "AI Summary",
    key: "full_summary",
    templateIndex: [1, 2, 3],
  },
  {
    id: "3",
    title: "History of Injury or Event/s",
    key: "history_of_injury",
    templateIndex: [1, 2, 3],
  },
  {
    id: "4",
    title: "Progress Over Time",
    key: "history_of_progress_over_time",
    templateIndex: [1, 2, 3],
  },
  {
    id: "5",
    title: "Current Symptoms and Presentation",
    key: "current_injury_status",
    templateIndex: [1, 2, 3],
  },
  {
    id: "6",
    title: "Pre-existing Medical History",
    key: "past_medical_history",
    templateIndex: [1, 2, 3],
  },
  {
    id: "7",
    title: "Personal, Social and Recreational Activities",
    key: "other_relevant_history",
    templateIndex: [1, 2, 3],
  },
  {
    id: "8",
    title: "Activities of Daily Living",
    key: "activities_of_daily_living",
    templateIndex: [1, 2, 3],
  },
  {
    id: "9",
    title: "Treatment and Medications",
    key: "medication",
    templateIndex: [1, 2, 3],
  },
  {
    id: "10",
    title: "Educational and Occupational History",
    key: "educational_occupational_background",
    templateIndex: [1, 2, 3],
  },
  {
    id: "11.1",
    title: "Examination",
    key: "physical_examination",
    templateIndex: [1, 3],
  },
  {
    // "11.2" is only used by the legacy template-2 (psychiatric) fallback path.
    // Service-type lists never include it — all 11 spec service types show a single
    // "Examination" tab mapped to physical_examination ("11.1"), per the spec.
    id: "11.2",
    title: "Examination",
    key: "mental_state_examination",
    templateIndex: [2],
  },
  {
    id: "12",
    title: "Assessment Notes",
    key: "notes",
    // No templateIndex needed — pinLast appends it on every path (MDRS-136).
    pinLast: true,
  },

  // ── Sections shared across multiple service types ─────────────────────────
  {
    id: "psych_history",
    title: "Psychological History",
    key: "mental_state_examination",
  },
  // The keys below point at per-service section columns added in migration
  // aiscribe001a. The VM populates them via `prompts/_legacy_column_mapping.json`
  // for the service types that map a slug to them — others will show empty
  // content until the VM extends its mapping for that service type.
  {
    id: "work_status",
    title: "Work Status and Capacity",
    key: "work_status_and_capacity",
  },

  // ── Capacity Assessment ───────────────────────────────────────────────────
  {
    id: "ca_condition_history",
    title: "Nature and History of the Condition Limiting Capacity",
    key: "history_of_injury",
  },
  {
    id: "ca_psych_factors",
    title: "Psychological Factors Affecting Capacity",
    key: "psychological_factors_affecting_capacity",
  },
  {
    id: "ca_physical_capacity",
    title: "Current Physical Capacity",
    key: "current_physical_capacity",
  },
  {
    id: "ca_cognitive_capacity",
    title: "Current Cognitive Capacity",
    key: "current_cognitive_capacity",
  },
  {
    id: "ca_work_capacity",
    title: "Current Work Capacity as Reported",
    key: "current_work_capacity_as_reported",
  },
  {
    id: "ca_rehab",
    title: "Rehabilitation Potential and Barriers",
    key: "rehabilitation_potential_and_barriers",
  },
  {
    id: "ca_adl_indicators",
    title: "Activities of Daily Living as Functional Indicators",
    key: "activities_of_daily_living",
  },

  // ── Comcare ───────────────────────────────────────────────────────────────
  {
    id: "med_since_injury",
    title: "Medical History Since the Injury",
    key: "history_of_treatment",
  },
  {
    id: "comcare_work_status",
    title: "Current Work Status and Capacity",
    key: "work_status_and_capacity",
  },

  // ── DVA ───────────────────────────────────────────────────────────────────
  {
    id: "dva_military",
    title: "Military Service History",
    key: "military_service_history",
  },
  {
    id: "dva_preservice",
    title: "Pre-service Background and Baseline",
    key: "pre_service_background_and_baseline",
  },
  {
    id: "dva_service_injuries",
    title: "Service-related Injuries, Events and Exposures",
    key: "history_of_injury",
  },
  {
    id: "dva_postservice",
    title: "Post-service Transition and Occupational History",
    key: "educational_occupational_background",
  },
  {
    id: "dva_comorbid",
    title: "Pre-existing and Comorbid Medical History",
    key: "past_medical_history",
  },
  {
    id: "dva_adl_social",
    title: "Activities of Daily Living, Relationships and Social Functioning",
    key: "activities_of_daily_living",
  },

  // ── Fitness for Duty ──────────────────────────────────────────────────────
  {
    id: "ffd_health_concern",
    title: "Nature of Presenting Health Concern",
    key: "history_of_injury",
  },
  {
    id: "medical_since_onset",
    title: "Medical History Since Onset",
    key: "history_of_treatment",
  },
  {
    id: "ffd_work_status",
    title: "Current Work Status",
    key: "work_status_and_capacity",
  },

  // ── Institutional Abuse ───────────────────────────────────────────────────
  {
    id: "ia_early_life",
    title: "Early Life and Developmental History",
    key: "early_life_and_developmental_history",
  },
  {
    id: "ia_abuse_history",
    title: "History of the Reported Abuse",
    key: "history_of_injury",
  },
  { id: "ia_disclosure", title: "Disclosure History", key: "disclosure_history" },
  {
    id: "ia_med_since",
    title: "Medical and Mental Health History Since the Abuse",
    key: "history_of_treatment",
  },
  {
    id: "ia_personal_social",
    title: "Personal, Social and Relationship History",
    key: "other_relevant_history",
  },

  // ── IP/TPD ────────────────────────────────────────────────────────────────
  {
    id: "iptpd_pre_disability",
    title: "Pre-disability Medical History",
    key: "past_medical_history",
  },
  {
    id: "iptpd_onset",
    title: "Nature and Onset of the Disabling Condition",
    key: "history_of_injury",
  },
  {
    id: "iptpd_work_capacity",
    title: "Current Work Status and Functional Capacity",
    key: "work_status_and_capacity",
  },

  // ── Medical Negligence ────────────────────────────────────────────────────
  {
    id: "mn_pretreatment",
    title: "Pre-existing Condition and Pre-Treatment Baseline",
    key: "past_medical_history",
  },
  {
    id: "mn_treating_rel",
    title: "The Treating Relationship and Informed Consent",
    key: "treating_relationship_and_informed_consent",
  },
  {
    id: "mn_alleged_event",
    title: "History of the Alleged Negligent Treatment or Event",
    key: "history_of_injury",
  },
  {
    id: "mn_immediate",
    title: "Immediate Consequences and Early Management",
    key: "immediate_consequences_and_early_management",
  },
  {
    id: "mn_med_since",
    title: "Medical History Since the Alleged Event",
    key: "history_of_treatment",
  },

  // ── Motor Vehicle ─────────────────────────────────────────────────────────
  {
    id: "mv_accident",
    title: "History of the Motor Vehicle Accident",
    key: "history_of_injury",
  },
  {
    id: "mv_med_since",
    title: "Medical History Since the Accident",
    key: "history_of_treatment",
  },

  // ── Pain and Suffering Quantum ────────────────────────────────────────────
  { id: "ps_preinjury", title: "Pre-injury Baseline", key: "past_medical_history" },
  {
    id: "ps_injury_overview",
    title: "History of Injury and Treatment Overview",
    key: "history_of_injury",
  },
  {
    id: "ps_pain_experience",
    title: "Current Pain Experience",
    key: "current_injury_status",
  },
  {
    id: "ps_psych_impact",
    title: "Psychological and Emotional Impact of Ongoing Pain and Injury",
    key: "psychological_and_emotional_impact",
  },
  { id: "ps_sleep_fatigue", title: "Sleep and Fatigue", key: "sleep_and_fatigue" },
  {
    id: "ps_relationships",
    title: "Impact on Relationships and Intimacy",
    key: "impact_on_relationships_and_intimacy",
  },
  {
    id: "ps_recreation",
    title: "Loss of Enjoyment of Life and Recreational Activities",
    key: "other_relevant_history",
  },
  {
    id: "ps_work_status",
    title: "Work Status and Occupational Impact",
    key: "work_status_and_capacity",
  },
  {
    id: "ps_prognosis",
    title: "Prognosis and Ongoing Suffering",
    key: "prognosis_and_ongoing_suffering",
  },

  // ── Public Liability ──────────────────────────────────────────────────────
  { id: "pl_incident", title: "History of the Incident", key: "history_of_injury" },
  {
    id: "pl_med_since",
    title: "Medical History Since the Incident",
    key: "history_of_treatment",
  },

  // ── Workers Compensation ──────────────────────────────────────────────────
  {
    id: "wc_med_since",
    title: "Medical History Since Injury",
    key: "history_of_treatment",
  },
];

// ─── Section IDs per service type (ordered) ──────────────────────────────────
// TabLinks uses this to render the correct section list based on case.service_type.

export const SECTIONS_BY_SERVICE_TYPE: Record<ServiceTypeKey, string[]> = {
  capacity_assessment: [
    "1",
    "2",
    "10",
    "6",
    "7",
    "ca_condition_history",
    "ca_psych_factors",
    "ca_physical_capacity",
    "ca_cognitive_capacity",
    "9",
    "ca_work_capacity",
    "ca_rehab",
    "ca_adl_indicators",
    "11.1",
  ],
  comcare: [
    "1",
    "2",
    "10",
    "6",
    "7",
    "3",
    "med_since_injury",
    "psych_history",
    "5",
    "9",
    "comcare_work_status",
    "8",
    "11.1",
  ],
  dva: [
    "1",
    "2",
    "dva_military",
    "dva_preservice",
    "dva_service_injuries",
    "dva_postservice",
    "dva_comorbid",
    "psych_history",
    "5",
    "9",
    "work_status",
    "dva_adl_social",
    "11.1",
  ],
  fitness_for_duty: [
    "1",
    "2",
    "10",
    "6",
    "7",
    "ffd_health_concern",
    "medical_since_onset",
    "psych_history",
    "5",
    "9",
    "ffd_work_status",
    "8",
    "11.1",
  ],
  institutional_abuse: [
    "1",
    "2",
    "ia_early_life",
    "10",
    "ia_abuse_history",
    "ia_disclosure",
    "ia_med_since",
    "ia_personal_social",
    "psych_history",
    "5",
    "9",
    "work_status",
    "8",
    "11.1",
  ],
  ip_tpd: [
    "1",
    "2",
    "10",
    "iptpd_pre_disability",
    "7",
    "iptpd_onset",
    "medical_since_onset",
    "psych_history",
    "5",
    "iptpd_work_capacity",
    "9",
    "8",
    "11.1",
  ],
  medical_negligence: [
    "1",
    "2",
    "mn_pretreatment",
    "10",
    "7",
    "mn_treating_rel",
    "mn_alleged_event",
    "mn_immediate",
    "mn_med_since",
    "psych_history",
    "5",
    "9",
    "work_status",
    "8",
    "11.1",
  ],
  motor_vehicle: [
    "1",
    "2",
    "10",
    "6",
    "7",
    "mv_accident",
    "mv_med_since",
    "psych_history",
    "5",
    "9",
    "work_status",
    "8",
    "11.1",
  ],
  pain_suffering_quantum: [
    "1",
    "2",
    "ps_preinjury",
    "10",
    "ps_injury_overview",
    "ps_pain_experience",
    "ps_psych_impact",
    "ps_sleep_fatigue",
    "ps_relationships",
    "ps_recreation",
    "9",
    "ps_work_status",
    "8",
    "ps_prognosis",
    "11.1",
  ],
  public_liability: [
    "1",
    "2",
    "10",
    "6",
    "7",
    "pl_incident",
    "pl_med_since",
    "psych_history",
    "5",
    "9",
    "work_status",
    "8",
    "11.1",
  ],
  workers_compensation: [
    "1",
    "2",
    "10",
    "6",
    "7",
    "3",
    "wc_med_since",
    "psych_history",
    "5",
    "9",
    "work_status",
    "8",
    "11.1",
  ],
};

// Assessment fields cleared when a transcript is overwritten (MDRS-132); mirrors
// the backend RESETTABLE_ASSESSMENT_COLUMNS. Typed so TS flags a stale column name.
// segmented_full_transcript isn't in the enum, so the caller clears it separately.
export const RESETTABLE_ASSESSMENT_FIELDS: AssessmentColumnNameEnum[] = [
  "full_transcript",
  "one_line_summary",
  "full_summary",
  "history_of_injury",
  "history_of_treatment",
  "history_of_progress_over_time",
  "other_relevant_history",
  "educational_occupational_background",
  "activities_of_daily_living",
  "medication",
  "physical_examination",
  "mental_state_examination",
  "current_injury_status",
  "past_medical_history",
  "psychological_history",
  "work_status_and_capacity",
  "psychological_factors_affecting_capacity",
  "current_physical_capacity",
  "current_cognitive_capacity",
  "current_work_capacity_as_reported",
  "rehabilitation_potential_and_barriers",
  "military_service_history",
  "pre_service_background_and_baseline",
  "early_life_and_developmental_history",
  "disclosure_history",
  "treating_relationship_and_informed_consent",
  "immediate_consequences_and_early_management",
  "psychological_and_emotional_impact",
  "sleep_and_fatigue",
  "impact_on_relationships_and_intimacy",
  "prognosis_and_ongoing_suffering",
  "notes",
];

// Derive ASSESSMENT_STATUS from the generated API type
export type AssessmentStatus = AssessmentResultStatusEnum;

export const ASSESSMENT_STATUS = {
  TO_BE_PROCESSED: "TO_BE_PROCESSED",
  TRANSCRIBING: "TRANSCRIBING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  EDITED: "EDITED",
  ACCEPTED: "ACCEPTED",
  EXPORTED: "EXPORTED",
} as const;
